import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { getPromptConfig, savePromptConfig } from '../../services/configService';

const MAX_CHARS = 10000;

const monoStyle = {
  background: 'rgba(15,23,42,0.7)',
  border: '1px solid rgba(71,158,195,0.2)',
  borderRadius: 10,
  color: '#a5f3fc',
  fontSize: 13.5,
  lineHeight: 1.7,
  fontFamily: "'SF Mono','Fira Code','Cascadia Code',monospace",
  outline: 'none',
  boxSizing: 'border-box',
  overflowY: 'auto',
  transition: 'border-color .18s',
  resize: 'none',
  width: '100%',
};

function SaveRow({ onSave, saving, isDirty, showSaved, error }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={onSave}
          disabled={!isDirty || saving}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '10px 24px', borderRadius: 9, border: 'none',
            cursor: !isDirty || saving ? 'not-allowed' : 'pointer',
            background: !isDirty || saving ? 'rgba(30,41,59,0.6)' : 'linear-gradient(135deg,#479EC3,#22D3EE)',
            color: !isDirty || saving ? '#334155' : '#fff',
            fontSize: 14, fontWeight: 700,
            transition: 'background .2s',
          }}
        >
          Save
        </button>

        <span style={{
          fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, minWidth: 72,
          color: saving ? '#64748b' : '#10b981',
          opacity: saving || showSaved ? 1 : 0,
          transition: 'opacity .2s',
          pointerEvents: 'none',
        }}>
          {saving ? (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                style={{ animation: 'spin 0.8s linear infinite', flexShrink: 0 }}>
                <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
              </svg>
              Saving…
            </>
          ) : (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ flexShrink: 0 }}>
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Saved
            </>
          )}
        </span>
      </div>

      {error && (
        <p style={{ fontSize: 13, color: '#f87171', margin: 0, padding: '8px 12px', borderRadius: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          {error}
        </p>
      )}
    </div>
  );
}

function ExpandModal({ prompt, onChange, onClose, onSave, saving, isDirty, showSaved, error }) {
  const textareaRef = useRef(null);

  useEffect(() => {
    textareaRef.current?.focus();
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.96, y: 8 }}
        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 1080,
          background: 'rgba(15,23,42,0.98)', backdropFilter: 'blur(24px)',
          border: '1px solid rgba(71,158,195,0.25)', borderRadius: 16,
          padding: 32, display: 'flex', flexDirection: 'column', gap: 16,
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#479EC3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/>
            </svg>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#f1f5f9' }}>System Prompt</span>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#475569', padding: 4, lineHeight: 1, transition: 'color .15s',
          }}
            onMouseEnter={e => e.currentTarget.style.color = '#94a3b8'}
            onMouseLeave={e => e.currentTarget.style.color = '#475569'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <textarea
          ref={textareaRef}
          value={prompt}
          onChange={e => onChange(e.target.value)}
          maxLength={MAX_CHARS}
          spellCheck={false}
          style={{ ...monoStyle, padding: '16px 18px', height: 420 }}
          onFocus={e => { e.target.style.borderColor = 'rgba(71,158,195,0.55)'; }}
          onBlur={e => { e.target.style.borderColor = 'rgba(71,158,195,0.2)'; }}
        />
        <p style={{ fontSize: 11.5, color: '#334155', textAlign: 'right', margin: 0 }}>{prompt.length}/{MAX_CHARS.toLocaleString()}</p>

        <SaveRow onSave={onSave} saving={saving} isDirty={isDirty} showSaved={showSaved} error={error} />
      </motion.div>
    </motion.div>,
    document.body
  );
}

export default function PromptConfig() {
  const { user } = useAuth();
  const [prompt, setPrompt]     = useState('');
  const [saved, setSaved]       = useState('');
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    getPromptConfig()
      .then(({ config }) => {
        const p = config?.systemPrompt || '';
        setPrompt(p);
        setSaved(p);
      })
      .catch(() => setError('Failed to load prompt config.'))
      .finally(() => setLoading(false));
  }, [user]);

  const isDirty   = prompt !== saved;
  const showSaved = !isDirty && !saving && saved !== '';

  async function handleSave() {
    setError('');
    setSaving(true);
    try {
      await savePromptConfig(prompt);
      setSaved(prompt);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#475569', fontSize: 13 }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
        style={{ animation: 'spin 0.8s linear infinite' }}>
        <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0" opacity=".25"/><path d="M21 12a9 9 0 0 0-9-9"/>
      </svg>
      Loading…
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p style={{ fontSize: 13, color: '#64748b' }}>
        Define the core instructions for your AI agent — its role, constraints, tone, and how it should handle edge cases.
      </p>

      <div style={{ position: 'relative' }}>
        <textarea
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          maxLength={MAX_CHARS}
          spellCheck={false}
          placeholder="You are a helpful AI support agent…"
          style={{ ...monoStyle, minHeight: 200, maxHeight: 360, padding: '14px 44px 14px 16px', resize: 'vertical' }}
          onFocus={e => { e.target.style.borderColor = 'rgba(71,158,195,0.55)'; }}
          onBlur={e => { e.target.style.borderColor = 'rgba(71,158,195,0.2)'; }}
        />
        <button
          onClick={() => setExpanded(true)}
          title="Expand editor"
          style={{
            position: 'absolute', top: 10, right: 10,
            background: 'rgba(71,158,195,0.1)', border: '1px solid rgba(71,158,195,0.2)',
            borderRadius: 6, cursor: 'pointer', padding: '4px 5px', lineHeight: 1,
            color: '#479EC3', transition: 'background .15s, border-color .15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(71,158,195,0.2)'; e.currentTarget.style.borderColor = 'rgba(71,158,195,0.45)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(71,158,195,0.1)'; e.currentTarget.style.borderColor = 'rgba(71,158,195,0.2)'; }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/>
          </svg>
        </button>
      </div>

      <p style={{ fontSize: 11.5, color: '#334155', margin: 0, textAlign: 'right' }}>{prompt.length}/{MAX_CHARS.toLocaleString()}</p>

      <SaveRow onSave={handleSave} saving={saving} isDirty={isDirty} showSaved={showSaved} error={error} />

      <AnimatePresence>
        {expanded && (
          <ExpandModal
            prompt={prompt}
            onChange={setPrompt}
            onClose={() => setExpanded(false)}
            onSave={handleSave}
            saving={saving}
            isDirty={isDirty}
            showSaved={showSaved}
            error={error}
          />
        )}
      </AnimatePresence>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
